import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, Card, Dropdown, Field, Input, MessageBar, MessageBarBody, Option, SpinButton, Switch, Text, tokens } from "@fluentui/react-components";
import { PLANNING_PRESETS, WEEKDAYS, matchPreset, type PlanningPreset, type Weekday } from "@gymplan/shared";
import { api } from "../lib/api.ts";
import { useAuth } from "../lib/auth.tsx";
import { GymLookup, gymFields, type GymChoice } from "../components/GymLookup.tsx";
import { ConfirmButton } from "../components/ConfirmButton.tsx";
import { TIME_OPTIONS, WEEKDAY_LABEL } from "../lib/format.ts";

export function SettingsPage() {
  const { user, refresh } = useAuth();
  const t = user!.trainer!;
  const [f, setF] = useState({
    name: user!.name,
    phone: t.phone ?? "",
    sessionLengthMinutes: t.sessionLengthMinutes,
    availabilityRequestDay: t.availabilityRequestDay as Weekday,
    availabilityRequestTime: t.availabilityRequestTime,
    responseWindowHours: t.responseWindowHours,
    autoPublish: t.autoPublish,
    publishDeadlineDay: t.publishDeadlineDay as Weekday,
    publishDeadlineTime: t.publishDeadlineTime,
  });
  const [custom, setCustom] = useState(() => matchPreset(t) === "custom");
  const preset = matchPreset(f);
  const applyPreset = (k: PlanningPreset) => {
    const { label: _l, blurb: _b, ...timing } = PLANNING_PRESETS[k];
    setF({ ...f, ...timing });
    setCustom(false);
  };
  const [gymChoice, setGymChoice] = useState<GymChoice | null>(null);

  const saveProfile = useMutation({ mutationFn: () => api.patch("/api/trainers/me", f), onSuccess: () => void refresh() });
  const saveGyms = useMutation({
    mutationFn: () => api.put("/api/trainers/me/gyms", gymFields(gymChoice!)),
    onSuccess: () => {
      setGymChoice(null);
      void refresh();
    },
  });
  const rotate = useMutation({ mutationFn: () => api.post("/api/trainers/me/invite/rotate"), onSuccess: () => void refresh() });
  const billing = useQuery({ queryKey: ["billing"], queryFn: () => api.get<{ enabled: boolean; status: string }>("/api/billing") });
  const [pw, setPw] = useState({ current: "", next: "" });
  const [delPw, setDelPw] = useState("");
  const changePw = useMutation({ mutationFn: () => api.post("/api/auth/change-password", { currentPassword: pw.current, newPassword: pw.next }), onSuccess: () => setPw({ current: "", next: "" }) });
  const logoutAll = useMutation({ mutationFn: () => api.post<{ signedOut: number }>("/api/auth/logout-everywhere") });
  const delAccount = useMutation({ mutationFn: () => api.del("/api/auth/me", { password: delPw }), onSuccess: () => { window.location.href = "/login"; } });

  const section: React.CSSProperties = { padding: 20, display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 };

  return (
    <div style={{ maxWidth: 720 }}>
      <Text as="h1" size={800} weight="semibold" block style={{ marginBottom: 16 }}>
        Settings
      </Text>

      <Card style={section}>
        <Text weight="semibold" size={500}>Profile & schedule</Text>
        <Field label="Name">
          <Input value={f.name} onChange={(_, d) => setF({ ...f, name: d.value })} />
        </Field>
        <Field label="WhatsApp number" required hint="International format, e.g. +44 7700 900123 — check-in alerts come here">
          <Input type="tel" autoComplete="tel" value={f.phone} onChange={(_, d) => setF({ ...f, phone: d.value.replace(/\s+/g, "") })} />
        </Field>
        <Field label="Usual session length (minutes)">
          <SpinButton min={15} max={240} step={15} value={f.sessionLengthMinutes} onChange={(_, d) => setF({ ...f, sessionLengthMinutes: d.value ?? f.sessionLengthMinutes })} style={{ maxWidth: 140 }} />
        </Field>
        <Field label="When do you want to start to plan the week ahead? (recommend Thursday)" hint={preset === "custom" ? "Custom timings (below)" : PLANNING_PRESETS[preset].blurb}>
          <Dropdown value={preset === "custom" ? "Custom" : PLANNING_PRESETS[preset].label} selectedOptions={[preset]} onOptionSelect={(_, d) => (d.optionValue === "custom" ? setCustom(true) : applyPreset(d.optionValue as PlanningPreset))}>
            {(Object.keys(PLANNING_PRESETS) as PlanningPreset[]).map((k) => (
              <Option key={k} value={k} text={PLANNING_PRESETS[k].label}>
                {PLANNING_PRESETS[k].label} — {PLANNING_PRESETS[k].blurb}
              </Option>
            ))}
            <Option value="custom" text="Custom">Custom…</Option>
          </Dropdown>
        </Field>
        <Switch label="Publish automatically when everyone fits (otherwise I review first)" checked={f.autoPublish} onChange={(_, d) => setF({ ...f, autoPublish: d.checked })} />
        {(custom || preset === "custom") && (
          <>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Ask clients for availability on">
            <Dropdown value={WEEKDAY_LABEL[f.availabilityRequestDay]} selectedOptions={[f.availabilityRequestDay]} onOptionSelect={(_, d) => setF({ ...f, availabilityRequestDay: d.optionValue as Weekday })}>
              {WEEKDAYS.map((w) => (
                <Option key={w} value={w}>
                  {WEEKDAY_LABEL[w]}
                </Option>
              ))}
            </Dropdown>
          </Field>
          <Field label="At">
            <Dropdown value={f.availabilityRequestTime} selectedOptions={[f.availabilityRequestTime]} onOptionSelect={(_, d) => setF({ ...f, availabilityRequestTime: d.optionValue! })}>
              {TIME_OPTIONS.map((x) => (
                <Option key={x} value={x}>
                  {x}
                </Option>
              ))}
            </Dropdown>
          </Field>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Hours clients have to reply" hint="Then non-responders default to their usual times and the schedule is built">
            <SpinButton min={6} max={120} step={6} value={f.responseWindowHours} onChange={(_, d) => setF({ ...f, responseWindowHours: d.value ?? f.responseWindowHours })} />
          </Field>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="If still in review, publish anyway on" hint="The week before sessions start">
            <Dropdown value={WEEKDAY_LABEL[f.publishDeadlineDay]} selectedOptions={[f.publishDeadlineDay]} onOptionSelect={(_, d) => setF({ ...f, publishDeadlineDay: d.optionValue as Weekday })}>
              {WEEKDAYS.map((w) => (
                <Option key={w} value={w}>
                  {WEEKDAY_LABEL[w]}
                </Option>
              ))}
            </Dropdown>
          </Field>
          <Field label="At">
            <Dropdown value={f.publishDeadlineTime} selectedOptions={[f.publishDeadlineTime]} onOptionSelect={(_, d) => setF({ ...f, publishDeadlineTime: d.optionValue! })}>
              {TIME_OPTIONS.map((x) => (
                <Option key={x} value={x}>
                  {x}
                </Option>
              ))}
            </Dropdown>
          </Field>
        </div>
          </>
        )}
        <Button appearance="primary" onClick={() => saveProfile.mutate()} disabled={saveProfile.isPending} style={{ alignSelf: "flex-start" }}>
          Save
        </Button>
        {saveProfile.isSuccess && <Text style={{ color: tokens.colorPaletteGreenForeground1 }}>Saved.</Text>}
      </Card>

      <Card style={section}>
        <Text weight="semibold" size={500}>Your gym</Text>
        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Now: {t.gyms.find((g) => g.isPrimary)?.name ?? "not set"}</Text>
        <GymLookup value={gymChoice} onChange={setGymChoice} />
        <Button appearance="primary" onClick={() => saveGyms.mutate()} disabled={!gymChoice || saveGyms.isPending} style={{ alignSelf: "flex-start" }}>
          Save gym
        </Button>
        {saveGyms.isError && (
          <MessageBar intent="error">
            <MessageBarBody>{(saveGyms.error as Error).message}</MessageBarBody>
          </MessageBar>
        )}
      </Card>

      <Card style={section}>
        <Text weight="semibold" size={500}>Client sign-up link</Text>
        <Text size={200} style={{ wordBreak: "break-all" }}>{`${window.location.origin}/join/${t.inviteToken}`}</Text>
        <ConfirmButton appearance="outline" title="Generate a new sign-up link?" message="The old link stops working immediately." confirmLabel="Generate" onConfirm={() => rotate.mutate()} style={{ alignSelf: "flex-start" }}>
          Generate a new link
        </ConfirmButton>
      </Card>

      <Card style={section}>
        <Text weight="semibold" size={500}>Account & security</Text>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 8, alignItems: "end" }}>
          <Field label="Current password">
            <Input type="password" autoComplete="current-password" value={pw.current} onChange={(_, d) => setPw({ ...pw, current: d.value })} />
          </Field>
          <Field label="New password" hint="At least 10 characters">
            <Input type="password" autoComplete="new-password" value={pw.next} onChange={(_, d) => setPw({ ...pw, next: d.value })} />
          </Field>
          <Button onClick={() => changePw.mutate()} disabled={!pw.current || pw.next.length < 10 || changePw.isPending}>Change</Button>
        </div>
        {changePw.isSuccess && <Text size={200} style={{ color: tokens.colorPaletteGreenForeground1 }}>Password changed.</Text>}
        {changePw.isError && <Text size={200} style={{ color: tokens.colorPaletteRedForeground1 }}>{(changePw.error as Error).message}</Text>}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button size="small" onClick={() => logoutAll.mutate()}>Sign out of other devices{logoutAll.data ? ` (${logoutAll.data.signedOut} signed out)` : ""}</Button>
          <Button as="a" href="/api/auth/me/export" size="small" appearance="secondary">Download my account data</Button>
        </div>
        <Text weight="semibold" style={{ marginTop: 8 }}>Delete account</Text>
        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Erases your account and all your clients' data. Export first if you need a copy.</Text>
        <div style={{ display: "flex", gap: 8, alignItems: "end" }}>
          <Field label="Confirm with your password">
            <Input type="password" value={delPw} onChange={(_, d) => setDelPw(d.value)} />
          </Field>
          <ConfirmButton appearance="outline" disabled={!delPw} title="Delete your account?" message="This permanently erases your account, your clients and all schedules. It cannot be undone." confirmLabel="Delete everything" onConfirm={() => delAccount.mutate()}>
            Delete account
          </ConfirmButton>
        </div>
        {delAccount.isError && <Text size={200} style={{ color: tokens.colorPaletteRedForeground1 }}>{(delAccount.error as Error).message}</Text>}
        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
          <a href="/privacy/trainers" target="_blank">Privacy notice</a> · <a href="/privacy/terms" target="_blank">Terms & data processing</a>
        </Text>
      </Card>

      <Card style={section}>
        <Text weight="semibold" size={500}>Billing</Text>
        <Text>
          Plan status: <b>{billing.data?.status ?? "…"}</b>
          {billing.data && !billing.data.enabled && " — billing isn't switched on yet; RackUp is free while in trial."}
        </Text>
        <Button appearance="secondary" disabled={!billing.data?.enabled} style={{ alignSelf: "flex-start" }}>
          Manage subscription
        </Button>
      </Card>
    </div>
  );
}
